import React, { useState, useEffect, useRef, useCallback } from 'react';
import Tree from 'react-d3-tree';
import { parseGedcomToTree, convertToD3Tree, findRootPersons, searchPersons, getPersonDetails } from '../utils/gedcomParser';
import { downloadGedcomFile, saveToLocalStorage, loadFromLocalStorage, hasStoredChanges, clearStoredData } from '../utils/gedcomExporter';
import PersonDetailsModal from './PersonDetailsModal';
import RelationshipFinder from './RelationshipFinder';
import './FamilyTree.css';

const FamilyTree = ({ onClose }) => {
  const [treeData, setTreeData] = useState(null);
  const [gedcomData, setGedcomData] = useState(null);
  const [rootPersons, setRootPersons] = useState([]);
  const [selectedRoot, setSelectedRoot] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const treeContainer = useRef(null);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  
  // Enhanced features state
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [showPersonDetails, setShowPersonDetails] = useState(false);
  const [showRelationshipFinder, setShowRelationshipFinder] = useState(false);
  const [filters, setFilters] = useState({
    generation: 'all',
    gender: 'all',
    location: 'all',
    dateRange: 'all'
  });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showSaveOptions, setShowSaveOptions] = useState(false);
  const [availableLocations, setAvailableLocations] = useState([]);

  const loadGedcomFile = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Check for stored changes first
      const storedData = loadFromLocalStorage();
      let parsed;
      
      if (storedData && storedData.gedcomContent) {
        // Use stored data if available
        console.log('Loading stored changes from localStorage');
        parsed = parseGedcomToTree(storedData.gedcomContent);
        setHasUnsavedChanges(true);
      } else {
        // Load the original GEDCOM file from public/data folder
        const response = await fetch('/data/rossetti.ged');
        if (!response.ok) {
          throw new Error('Failed to load family tree data');
        }
        
        const content = await response.text();
        parsed = parseGedcomToTree(content);
        setHasUnsavedChanges(false);
      }
      
      setGedcomData(parsed);
      
      // Extract unique locations for filter dropdown
      const locations = extractUniqueLocations(parsed);
      setAvailableLocations(locations);
      
      const roots = findRootPersons(parsed);
      setRootPersons(roots);
      
      if (roots.length > 0) {
        // Try to find Pasquale Rossetti as the default root, otherwise use first root
        const pasqualeRoot = roots.find(person => 
          person.name.toLowerCase().includes('pasquale') && 
          person.name.toLowerCase().includes('rossetti')
        );
        const defaultRoot = pasqualeRoot || roots[0];
        
        setSelectedRoot(defaultRoot.id);
        const tree = convertToD3Tree(parsed, defaultRoot.id);
        setTreeData(tree);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error loading GEDCOM file:', err);
      setError('Failed to load Rossetti family tree data');
      setLoading(false);
    }
  }, []); // Empty dependency array since this function doesn't depend on any state

  // Load GEDCOM file on component mount
  useEffect(() => {
    loadGedcomFile();
  }, [loadGedcomFile]);

  // Center the tree when container is ready
  useEffect(() => {
    if (treeContainer.current) {
      const dimensions = treeContainer.current.getBoundingClientRect();
      setTranslate({
        x: dimensions.width / 2,
        y: 100
      });
    }
  }, [treeData]);

  const handleRootChange = (personId) => {
    if (gedcomData && personId) {
      setSelectedRoot(personId);
      const tree = convertToD3Tree(gedcomData, personId);
      setTreeData(tree);
    }
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    if (gedcomData && term) {
      const results = searchPersons(gedcomData, term);
      setSearchResults(results);
    } else {
      setSearchResults([]);
    }
  };

  const handleSearchSelect = (personId) => {
    handleRootChange(personId);
    setSearchTerm('');
    setSearchResults([]);
  };

  // Enhanced node click handler
  const handleNodeClick = (nodeData) => {
    if (gedcomData && nodeData.attributes?.id) {
      const personDetails = getPersonDetails(gedcomData, nodeData.attributes.id);
      setSelectedPerson(personDetails);
      setShowPersonDetails(true);
    }
  };

  // Handle person selection from modal
  const handlePersonSelect = (person) => {
    setSelectedPerson(person);
    // Optionally change the tree root to this person
    if (person.id) {
      handleRootChange(person.id);
    }
  };

  // Handle saving edited person data
  const handleSaveEdit = async (personId, updatedPerson) => {
    try {
      // Update the person in the gedcomData
      if (gedcomData && gedcomData.individuals[personId]) {
        Object.assign(gedcomData.individuals[personId], updatedPerson);
        
        // Update the selected person state
        setSelectedPerson(updatedPerson);
        
        // Regenerate the tree to reflect changes
        if (selectedRoot) {
          const tree = convertToD3Tree(gedcomData, selectedRoot);
          setTreeData(tree);
        }
        
        // Update available locations in case new places were added
        const locations = extractUniqueLocations(gedcomData);
        setAvailableLocations(locations);
        
        // Save to localStorage for persistence
        const saveSuccess = saveToLocalStorage(gedcomData);
        if (saveSuccess) {
          setHasUnsavedChanges(true);
          console.log('Person data updated and saved to localStorage:', updatedPerson);
        } else {
          console.warn('Failed to save to localStorage, changes are only in memory');
        }
        
        return Promise.resolve();
      }
    } catch (error) {
      console.error('Error saving person data:', error);
      throw error;
    }
  };

  // Handle downloading GEDCOM file
  const handleDownloadGedcom = () => {
    if (gedcomData) {
      const success = downloadGedcomFile(gedcomData, 'rossetti_updated.ged');
      if (success) {
        setHasUnsavedChanges(false);
        alert('GEDCOM file downloaded successfully! Your changes have been saved.');
      } else {
        alert('Error downloading GEDCOM file. Please try again.');
      }
    }
  };

  // Handle clearing stored changes
  const handleClearChanges = () => {
    if (window.confirm('Are you sure you want to discard all changes and reload the original data?')) {
      clearStoredData();
      setHasUnsavedChanges(false);
      loadGedcomFile(); // Reload original data
    }
  };

  // Extract unique locations from GEDCOM data
  const extractUniqueLocations = (gedcomData) => {
    if (!gedcomData || !gedcomData.individuals) return [];
    
    const locations = new Set();
    
    Object.values(gedcomData.individuals).forEach(person => {
      // Add birth places
      if (person.birthPlace && person.birthPlace.trim()) {
        // Extract country/main location (last part after comma)
        const birthLocation = person.birthPlace.split(',').pop().trim();
        if (birthLocation) {
          locations.add(birthLocation);
        }
      }
      
      // Add death places
      if (person.deathPlace && person.deathPlace.trim()) {
        // Extract country/main location (last part after comma)
        const deathLocation = person.deathPlace.split(',').pop().trim();
        if (deathLocation) {
          locations.add(deathLocation);
        }
      }
    });
    
    // Convert to sorted array
    return Array.from(locations).sort();
  };

  // Custom node rendering with enhanced interactivity
  const renderCustomNode = ({ nodeDatum, toggleNode }) => (
    <g>
      <circle
        r={22}
        fill={nodeDatum.attributes?.sex === 'M' ? '#4A90E2' : '#E24A90'}
        stroke={selectedPerson?.id === nodeDatum.attributes?.id ? '#FFD700' : '#333'}
        strokeWidth={selectedPerson?.id === nodeDatum.attributes?.id ? '3' : '2'}
        onClick={(e) => {
          e.stopPropagation();
          handleNodeClick(nodeDatum);
        }}
        onDoubleClick={toggleNode}
        style={{ cursor: 'pointer' }}
      />
      <text
        fill="white"
        strokeWidth="0"
        x="0"
        y="5"
        textAnchor="middle"
        fontSize="12"
        fontWeight="bold"
        style={{ pointerEvents: 'none' }}
      >
        {nodeDatum.name.split(' ')[0]}
      </text>
      <text
        fill="#333"
        strokeWidth="0"
        x="0"
        y="40"
        textAnchor="middle"
        fontSize="10"
        style={{ maxWidth: '120px', pointerEvents: 'none' }}
      >
        {nodeDatum.name}
      </text>
      {nodeDatum.attributes?.birth && (
        <text
          fill="#666"
          strokeWidth="0"
          x="0"
          y="55"
          textAnchor="middle"
          fontSize="8"
          style={{ pointerEvents: 'none' }}
        >
          b. {nodeDatum.attributes.birth}
        </text>
      )}
      {nodeDatum.attributes?.death && (
        <text
          fill="#666"
          strokeWidth="0"
          x="0"
          y="68"
          textAnchor="middle"
          fontSize="8"
          style={{ pointerEvents: 'none' }}
        >
          d. {nodeDatum.attributes.death}
        </text>
      )}
      {/* Enhanced visual indicators */}
      {nodeDatum.children && nodeDatum.children.length > 0 && (
        <circle
          cx="18"
          cy="-18"
          r="6"
          fill="#28a745"
          stroke="white"
          strokeWidth="1"
          style={{ pointerEvents: 'none' }}
        />
      )}
    </g>
  );

  if (loading) {
    return (
      <div className="family-tree-modal">
        <div className="family-tree-content">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading Rossetti family tree...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="family-tree-modal">
        <div className="family-tree-content">
          <div className="error-message">
            <h3>Error Loading Family Tree</h3>
            <p>{error}</p>
            <button onClick={onClose} className="cta-btn primary">
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="family-tree-modal">
      <div className="family-tree-content">
        <div className="family-tree-header">
          <div className="header-left">
            <h2>Rossetti Family Tree</h2>
            {hasUnsavedChanges && (
              <span className="unsaved-indicator">
                <i className="fas fa-circle"></i>
                Unsaved Changes
              </span>
            )}
          </div>
          <div className="header-actions">
            {hasUnsavedChanges && (
              <button 
                className="save-gedcom-btn"
                onClick={handleDownloadGedcom}
                title="Download updated GEDCOM file"
              >
                <i className="fas fa-download"></i>
                Save GEDCOM
              </button>
            )}
            <button 
              className="save-options-btn"
              onClick={() => setShowSaveOptions(!showSaveOptions)}
              title="Save options"
            >
              <i className="fas fa-save"></i>
            </button>
            <button onClick={onClose} className="close-btn">×</button>
          </div>
        </div>

        {/* Save Options Panel */}
        {showSaveOptions && (
          <div className="save-options-panel">
            <h3><i className="fas fa-save"></i> Save Options</h3>
            <div className="save-options-grid">
              <button 
                className="save-option download-option"
                onClick={handleDownloadGedcom}
              >
                <i className="fas fa-download"></i>
                <div className="option-info">
                  <strong>Download GEDCOM File</strong>
                  <span>Save changes to a .ged file on your computer</span>
                </div>
              </button>
              
              <button 
                className="save-option storage-option"
                onClick={() => {
                  if (gedcomData) {
                    saveToLocalStorage(gedcomData);
                    alert('Changes saved to browser storage!');
                  }
                }}
              >
                <i className="fas fa-hdd"></i>
                <div className="option-info">
                  <strong>Save to Browser</strong>
                  <span>Keep changes in browser storage (temporary)</span>
                </div>
              </button>
              
              {hasUnsavedChanges && (
                <button 
                  className="save-option clear-option"
                  onClick={handleClearChanges}
                >
                  <i className="fas fa-undo"></i>
                  <div className="option-info">
                    <strong>Discard Changes</strong>
                    <span>Reload original data and lose all edits</span>
                  </div>
                </button>
              )}
            </div>
            
            <div className="save-info">
              <p><strong>💡 Tip:</strong> Download the GEDCOM file to permanently save your changes. Browser storage is temporary and may be cleared.</p>
              {hasStoredChanges() && (
                <p><strong>📁 Status:</strong> You have unsaved changes stored in your browser.</p>
              )}
            </div>
          </div>
        )}
        
        <div className="family-tree-controls">
          <div className="controls-row">
            <div className="control-group">
              <label>Root Person:</label>
              <select 
                value={selectedRoot} 
                onChange={(e) => handleRootChange(e.target.value)}
              >
                {rootPersons.map(person => (
                  <option key={person.id} value={person.id}>
                    {person.name} {person.birth && `(b. ${person.birth})`}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="control-group">
              <label>Search:</label>
              <div className="search-container">
                <div className="search-input-wrapper">
                  <i className="fas fa-search search-icon"></i>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => handleSearch(e.target.value)}
                    placeholder="Search for a family member..."
                  />
                  {searchTerm && (
                    <button 
                      className="clear-search"
                      onClick={() => {
                        setSearchTerm('');
                        setSearchResults([]);
                      }}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>
                {searchResults.length > 0 && (
                  <div className="search-results">
                    {searchResults.map(person => (
                      <div
                        key={person.id}
                        className="search-result"
                        onClick={() => handleSearchSelect(person.id)}
                      >
                        <i className="fas fa-user"></i>
                        {person.name} {person.birth && `(b. ${person.birth})`}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="control-group">
              <label>Filter by Gender:</label>
              <select 
                value={filters.gender} 
                onChange={(e) => setFilters({...filters, gender: e.target.value})}
              >
                <option value="all">All Genders</option>
                <option value="M">Male</option>
                <option value="F">Female</option>
              </select>
            </div>
          </div>

          <div className="controls-row">
            <div className="control-group">
              <label>Date Range:</label>
              <select 
                value={filters.dateRange} 
                onChange={(e) => setFilters({...filters, dateRange: e.target.value})}
              >
                <option value="all">All Periods</option>
                <option value="1800-1850">1800-1850</option>
                <option value="1850-1900">1850-1900</option>
                <option value="1900-1950">1900-1950</option>
                <option value="1950-2000">1950-2000</option>
                <option value="2000+">2000+</option>
              </select>
            </div>

            <div className="control-group">
              <label>Location:</label>
              <select 
                value={filters.location} 
                onChange={(e) => setFilters({...filters, location: e.target.value})}
              >
                <option value="all">All Locations</option>
                {availableLocations.map(location => (
                  <option key={location} value={location}>
                    {location}
                  </option>
                ))}
              </select>
            </div>

            <div className="control-group">
              <button 
                className="tool-btn relationship-btn"
                onClick={() => setShowRelationshipFinder(true)}
              >
                <i className="fas fa-route"></i>
                Find Relationship
              </button>
            </div>
          </div>
        </div>

        <div className="family-tree-container" ref={treeContainer}>
          {treeData && (
            <Tree
              data={treeData}
              translate={translate}
              orientation="vertical"
              pathFunc="diagonal"
              nodeSize={{ x: 200, y: 150 }}
              separation={{ siblings: 1, nonSiblings: 2 }}
              renderCustomNodeElement={renderCustomNode}
              zoom={0.8}
              scaleExtent={{ min: 0.1, max: 3 }}
              enableLegacyTransitions={true}
            />
          )}
        </div>
        
        <div className="family-tree-legend">
          <div className="legend-item">
            <div className="legend-circle male"></div>
            <span>Male</span>
          </div>
          <div className="legend-item">
            <div className="legend-circle female"></div>
            <span>Female</span>
          </div>
          <div className="legend-item">
            <div className="legend-circle has-children"></div>
            <span>Has Children</span>
          </div>
          <p className="legend-note">
            <strong>Click:</strong> View details • <strong>Double-click:</strong> Expand/collapse
          </p>
        </div>

        {/* Enhanced Modals */}
        {showPersonDetails && selectedPerson && (
          <PersonDetailsModal 
            person={selectedPerson}
            gedcomData={gedcomData}
            onClose={() => {
              setShowPersonDetails(false);
              setSelectedPerson(null);
            }}
            onFindRelationship={() => {
              setShowPersonDetails(false);
              setShowRelationshipFinder(true);
            }}
            onPersonSelect={handlePersonSelect}
            onSaveEdit={handleSaveEdit}
          />
        )}

        {showRelationshipFinder && (
          <RelationshipFinder
            gedcomData={gedcomData}
            onClose={() => setShowRelationshipFinder(false)}
            preselectedPerson={selectedPerson}
            onPersonSelect={(person) => {
              setSelectedPerson(person);
              setShowRelationshipFinder(false);
              setShowPersonDetails(true);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default FamilyTree;
